import axios from 'axios';
import { API_BASE as API_BASE_URL } from '../lib/api';


// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Read from both storages — supports session-only (rememberMe=false) and persistent sessions
    const token = localStorage.getItem('retailcliq_token') || sessionStorage.getItem('retailcliq_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear tokens from both storages to prevent the reload-rehydrate-401 loop
      ['retailcliq_token', 'retailcliq_user', 'rcq_access_token', 'rcq_user'].forEach(k => {
        try { localStorage.removeItem(k); sessionStorage.removeItem(k); } catch (_) {}
      });
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username, password, mfaCode = null) => {
    const response = await api.post('/api/auth/login', { username, password, mfaCode });
    if (response.data.success && response.data.token) {
      localStorage.setItem('retailcliq_token', response.data.token);
      localStorage.setItem('retailcliq_user', JSON.stringify(response.data.user));
      if (response.data.globalContext) {
        localStorage.setItem('retailcliq_global_context', JSON.stringify(response.data.globalContext));
      }
    }
    return response.data;
  },
  
  verify: async () => {
    const response = await api.get('/api/auth/verify');
    return response.data;
  },
  
  logout: async () => {
    try {
      await api.post('/api/auth/logout');
    } finally {
      localStorage.removeItem('retailcliq_token');
      localStorage.removeItem('retailcliq_user');
      localStorage.removeItem('retailcliq_global_context');
    }
  },
  
  refresh: async () => {
    const response = await api.post('/api/auth/refresh');
    if (response.data.success && response.data.token) {
      localStorage.setItem('retailcliq_token', response.data.token);
    }
    return response.data;
  },
  
  getStoredUser: () => {
    const user = localStorage.getItem('retailcliq_user');
    return user ? JSON.parse(user) : null;
  },
  
  isAuthenticated: () => {
    return !!localStorage.getItem('retailcliq_token');
  }
};

// Dashboard API
export const dashboardAPI = {
  getStats: async () => {
    const response = await api.get('/api/dashboard/stats');
    return response.data;
  },
  
  getRecentTransactions: async () => {
    const response = await api.get('/api/dashboard/recent-transactions');
    return response.data;
  },
  
  getAlerts: async () => {
    const response = await api.get('/api/dashboard/alerts');
    return response.data;
  },
  
  getDbStatus: async () => {
    const response = await api.get('/api/dashboard/db-status');
    return response.data;
  }
};

// Inventory API
export const inventoryAPI = {
  getItems: async () => {
    const response = await api.get('/api/inventory/items');
    return response.data;
  },
  
  getItem: async (id) => {
    const response = await api.get(`/api/inventory/items/${id}`);
    return response.data;
  },
  
  createItem: async (itemData) => {
    const response = await api.post('/api/inventory/items', itemData);
    return response.data;
  },
  
  updateItem: async (itemId, updates) => {
    const response = await api.put(`/api/inventory/items/${itemId}`, updates);
    return response.data;
  },
  
  getStock: async () => {
    const response = await api.get('/api/inventory/stock');
    return response.data;
  },
  
  adjustStock: async (adjustmentData) => {
    const response = await api.post('/api/inventory/stock/adjust', adjustmentData);
    return response.data;
  }
};

// Purchase API
export const purchaseAPI = {
  getInvoices: async () => {
    const response = await api.get('/api/purchase/invoices');
    return response.data;
  },
  
  getInvoice: async (invoiceId) => {
    const response = await api.get(`/api/purchase/invoices/${invoiceId}`);
    return response.data;
  },
  
  createInvoice: async (invoiceData) => {
    const response = await api.post('/api/purchase/invoices', invoiceData);
    return response.data;
  },
  
  updateInvoiceStatus: async (invoiceId, status) => {
    const response = await api.patch(`/api/purchase/invoices/${invoiceId}/status`, { status });
    return response.data;
  },
  
  getGRNList: async () => {
    const response = await api.get('/api/purchase/grn');
    return response.data;
  },
  
  createGRN: async (grnData) => {
    const response = await api.post('/api/purchase/grn', grnData);
    return response.data;
  }
};

// Masters API
export const mastersAPI = {
  // Divisions
  getDivisions: async () => {
    const response = await api.get('/api/masters/divisions');
    return response.data;
  },
  createDivision: async (data) => {
    const response = await api.post('/api/masters/divisions', data);
    return response.data;
  },
  
  // Departments
  getDepartments: async (divisionId = null) => {
    const url = divisionId ? `/api/masters/departments?divisionId=${divisionId}` : '/api/masters/departments';
    const response = await api.get(url);
    return response.data;
  },
  createDepartment: async (data) => {
    const response = await api.post('/api/masters/departments', data);
    return response.data;
  },
  
  // Categories
  getCategories: async (departmentId = null) => {
    const url = departmentId ? `/api/masters/categories?departmentId=${departmentId}` : '/api/masters/categories';
    const response = await api.get(url);
    return response.data;
  },
  createCategory: async (data) => {
    const response = await api.post('/api/masters/categories', data);
    return response.data;
  },
  
  // Brands
  getBrands: async () => {
    const response = await api.get('/api/masters/brands');
    return response.data;
  },
  createBrand: async (data) => {
    const response = await api.post('/api/masters/brands', data);
    return response.data;
  },
  
  // Suppliers
  getSuppliers: async () => {
    const response = await api.get('/api/masters/suppliers');
    return response.data;
  },
  getSupplier: async (supplierId) => {
    const response = await api.get(`/api/masters/suppliers/${supplierId}`);
    return response.data;
  },
  createSupplier: async (data) => {
    const response = await api.post('/api/masters/suppliers', data);
    return response.data;
  },
  
  // Warehouses
  getWarehouses: async () => {
    const response = await api.get('/api/masters/warehouses');
    return response.data;
  },
  createWarehouse: async (data) => {
    const response = await api.post('/api/masters/warehouses', data);
    return response.data;
  }
};

// Users API
export const usersAPI = {
  getAll: async () => {
    const response = await api.get('/api/users');
    return response.data;
  },
  
  getMe: async () => {
    const response = await api.get('/api/users/me');
    return response.data;
  },
  
  create: async (userData) => {
    const response = await api.post('/api/users', userData);
    return response.data;
  },
  
  update: async (userId, updates) => {
    const response = await api.put(`/api/users/${userId}`, updates);
    return response.data;
  }
};

export default api;
